import React, { useRef, useState, useMemo, useCallback, useContext } from 'react';
import capitalize from 'lodash/capitalize';
import isEqual from 'lodash/isEqual';
import { FormattedMessage } from 'react-intl-next';
import { styled } from '@compiled/react';
// We have deprecated unstated. Please use react-sweet-state instead

import { Subscribe } from 'unstated';

import { token } from '@atlaskit/tokens';
import AkProgressBar from '@atlaskit/progress-bar';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { ScreenEvent } from '@confluence/analytics/entry-points/ScreenEvent';
import { AtlassianSupportLink } from '@confluence/error-boundary/entry-points/AtlassianSupportLink';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext';
import { SPACE_ARCHIVED_PAGES } from '@confluence/named-routes';

import { fireUIClickEvent, getActionName } from './AnalyticsEvents';
import type { ArchiveStatusFlagI18n } from './archivingConstants';
import { ARCHIVE_INTL_PREFIX, ArchiveStatus, UNARCHIVE_INTL_PREFIX } from './archivingConstants';
import { i18n } from './flagI18n';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ProgressBarContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
	height: '32px',
	justifyContent: 'center',
	paddingRight: token('space.500'),
});

type Action = {
	content: JSX.Element;
	href: string;
	onClick: () => void;
};

const getTitle = (
	flagI18n: ArchiveStatusFlagI18n,
	currentArchiveStatus: ArchiveStatus,
	contentIds: string[],
) => {
	const TitleMap = {
		[ArchiveStatus.LOADING]: flagI18n.loadingTitle,
		[ArchiveStatus.ERROR]: flagI18n.errorTitle,
		[ArchiveStatus.SUCCESS]: flagI18n.successTitle,
	};

	const titleDescriptor = TitleMap[currentArchiveStatus];

	return <FormattedMessage {...titleDescriptor} values={{ count: contentIds.length }} />;
};

type ArchiveFlagsProps = {
	contentIds: string[];
	error?: Error;
	flagI18n: ArchiveStatusFlagI18n;
	isArchive: boolean;
	percentageComplete: number;
	isIndeterminate: boolean;
	successLinkHref: string;
	onSuccessLinkClick?(): void;
	isBulk: boolean;
	shouldUseAutomationDiscovery?: boolean;
};
export const ArchiveFlags = ({
	contentIds,
	error,
	flagI18n,
	isArchive,
	isIndeterminate,
	percentageComplete,
	successLinkHref,
	onSuccessLinkClick,
	isBulk,
	shouldUseAutomationDiscovery,
}: ArchiveFlagsProps) => {
	const [lastPercentageComplete, setLastPercentageComplete] = useState<number>(0);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [lastError, setLastError] = useState<boolean | null>(null);
	const flagIdRef = useRef<string | number | undefined>(undefined);

	const success: boolean = !error && percentageComplete === 100;
	const { getHref, matchRoute } = useContext(RoutesContext);
	const route = matchRoute(getHref());

	const intlPrefix = `${isArchive ? ARCHIVE_INTL_PREFIX : UNARCHIVE_INTL_PREFIX}${
		success
			? 'archiveContentFlagSuccess'
			: error
				? 'archiveContentFlagFailure'
				: 'archiveContentFlagProgress'
	}`;

	const actionName: string = getActionName(isArchive);
	const archiveStatus: ArchiveStatus =
		(error && ArchiveStatus.ERROR) || (success && ArchiveStatus.SUCCESS) || ArchiveStatus.LOADING;
	const flagName: string = (error && 'Error') || (success && 'Success') || 'Loading';
	const sourceName: string = `${actionName}Pages${flagName}Flag`;

	// Note the firing of the Screen event (aka, the <ScreenEvent> component) may need to be changed
	// to manually fire the event in a useEffect hook when progress bar updates are implemented, as
	// the title may end up getting rendered multiple times and the Screen event would be fired more
	// than once.  Also, "id" is a required prop for <ScreenEvent>, but is not actually sent in the
	// event
	const title: JSX.Element = (
		<>
			<ScreenEvent name={sourceName} id="" />
			{getTitle(flagI18n, archiveStatus, contentIds)}
		</>
	);

	const getDescription = (
		flagI18n: ArchiveStatusFlagI18n,
		currentArchiveStatus: ArchiveStatus,
		percentageComplete: number,
		error?: Error,
	): JSX.Element | '' => {
		switch (currentArchiveStatus) {
			case ArchiveStatus.ERROR: {
				const errorMsg = flagI18n.getErrorMessage(error);
				return (
					<FormattedMessage
						{...errorMsg}
						values={{
							tryLater: (
								<b>
									<FormattedMessage {...i18n.tryLater} />
								</b>
							),
							lineBreak: <br />,
							supportLink: <AtlassianSupportLink />,
						}}
					/>
				);
			}
			case ArchiveStatus.LOADING: {
				return (
					<ProgressBarContainer>
						<AkProgressBar
							ariaLabel={`Done: ${percentageComplete}% completed`}
							isIndeterminate={isIndeterminate}
							value={percentageComplete / 100}
						/>
					</ProgressBarContainer>
				);
			}
			case ArchiveStatus.SUCCESS: {
				return '';
			}
		}
	};

	const handleSuccessLinkClick = useCallback(() => {
		fireUIClickEvent({
			createAnalyticsEvent,
			actionSubject: 'link',
			// actionSubjectId should be either "viewArchivedPage" or "viewRestoredPage" but
			// actionName is either "archive" or "restore"
			actionSubjectId: `view${capitalize(actionName)}dPage`,
			source: sourceName,
		});
		onSuccessLinkClick && onSuccessLinkClick();
	}, [actionName, sourceName, createAnalyticsEvent, onSuccessLinkClick]);

	const successActions = useMemo(() => {
		const actions: Action[] = [];
		if (route?.name !== SPACE_ARCHIVED_PAGES.name && success) {
			actions.push({
				content: <FormattedMessage {...i18n[`${intlPrefix}_LinkText`]} />,
				href: successLinkHref,
				onClick: handleSuccessLinkClick,
			});

			if (isBulk) {
				actions.push({
					content: <FormattedMessage {...i18n.refresh_LinkText} />,
					href: '',
					onClick: () => window.location.reload(),
				});
			}
		}
		return actions;
	}, [success, isBulk, handleSuccessLinkClick, intlPrefix, successLinkHref, route]);

	return (
		<Subscribe to={[FlagsStateContainer]}>
			{(flags: FlagsStateContainer) => {
				if (
					// If we're using automation discovery, only show error flags
					// since the archive modal will show a success message
					// This avoids a situation where both the modal and flag show a success message
					// error !== lastError is required to prevent a render loop. See note at the end of this block
					shouldUseAutomationDiscovery
						? flagName === 'Error' && !!error !== lastError
						: // If the state of the archive/restore operation has changed, update the flag's contents
							percentageComplete !== lastPercentageComplete || !!error !== lastError
				) {
					void flags
						.showFlag({
							id: flagIdRef.current, // specifying the ID will update the existing flag
							type: success ? 'success-circle' : !!error ? 'error' : 'info',
							title,
							description: getDescription(flagI18n, archiveStatus, percentageComplete, error),
							actions: successActions,
						})
						.then((newFlag) => {
							// If a new flag was created, store the new flag's ID so we can update it.  Note that
							// `showFlag()` only asynchronously returns a valid newFlag if it created a new flag;
							// if it found an existing flag with the specified ID then it asynchronously returns
							// nothing (undefined).
							if (newFlag) {
								flagIdRef.current = newFlag.id;
							}
						});
					// Similar flags rendering issue noted in LinkToThisPageDialog:
					// Set the state to record that the data affecting the flag has been updated.  Without
					// this, we can get into an infinite rerendering loop that causes Chrome to crash.
					// (We only need to check the `error` variable, because React's state comparison thinks
					// two different object instances with the same members and values are different.  We
					// don't need to check primitive values like `percentageComplete` because React correctly
					// compares them.)
					isEqual(error, lastError) || setLastError(!!error);
					setLastPercentageComplete(percentageComplete);
				}
				return null;
			}}
		</Subscribe>
	);
};
